// ============================================================================
// FSA NOTE MODAL — DEVIATIONS PANEL  (FSA-2.4 N1)
// ============================================================================
// Single, consolidated exception summary that lives at the top of a Note's
// main body. Replaces the scattered chips that used to live in the header
// (ReconPill), the toolbar (ReconChip + Tolerance pill), and per-class /
// per-line rows (drift chips). Auditor reads ONE place to see "what needs
// my attention on this Note" instead of scanning four zones of badges.
//
// Auto-hides to a compact "All checks passed" pill when no exception is
// active. Expands into a structured list when one or more fire.
//
// N1 ships the SCAFFOLD: drift (CY/PY), cross-note movement summary,
// pending refresh signals (ledger + cross-note sources). N3 plugs in
// the v2 drift evaluator (cross-note both directions + sign-sweep +
// PL→Reserves) so this panel becomes the authoritative exception view.
//
// The panel takes already-computed signals as props. It does NOT
// recompute drift itself — that math lives in the pane and modal so
// every consumer reads consistent numbers.

import React, { useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';

export type NoteDeviationSeverity = 'info' | 'warning' | 'error';

export interface NoteDeviationItem {
  /** Stable key — used for React reconciliation + e2e selectors. */
  id: string;
  severity: NoteDeviationSeverity;
  /** Short headline; aim for ~60 chars. */
  label: string;
  /** Optional detail — rendered as muted right-aligned text. */
  detail?: string;
}

interface Props {
  items: NoteDeviationItem[];
  /** Initial expansion state. Defaults to expanded when items exist. */
  defaultExpanded?: boolean;
}

const NoteDeviationPanel: React.FC<Props> = ({ items, defaultExpanded = true }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  // Empty state — auto-collapsed reassurance pill. Avoids the noisy
  // "0 items" header pattern other dashboards fall into.
  if (items.length === 0) {
    return (
      <div style={emptyStateStyle} title="No reconciliation drift, no pending refresh, no cross-note inconsistencies.">
        <IconSystem.check size={13} />
        <span>All checks passed</span>
      </div>
    );
  }

  // Error severity drives the border colour — a red item means a
  // material reconciliation gap; warnings are auditor-actionable but
  // don't block submission. (The block-on-submit check lives elsewhere
  // and reads the actual drift number, not this panel's content.)
  const hasError = items.some(i => i.severity === 'error');

  return (
    <section style={panelStyle(hasError)}>
      <button
        type="button"
        onClick={() => setExpanded(e => !e)}
        style={headerStyle}
        aria-expanded={expanded}
      >
        <span style={headerLabelStyle}>
          <IconSystem.alert size={14} /> Deviations
        </span>
        <span style={countPillStyle(hasError)}>
          {items.length} item{items.length === 1 ? '' : 's'}
        </span>
        <span style={{ flex: 1 }} />
        <IconSystem.chevronDown
          size={14}
          style={{
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 120ms',
            color: 'var(--text-secondary)',
          }}
        />
      </button>
      {expanded && (
        <ul style={listStyle}>
          {items.map(item => (
            <li key={item.id} style={itemRowStyle}>
              <span style={severityIconStyle(item.severity)}>
                {item.severity === 'error' ? '⛔' : item.severity === 'warning' ? '⚠' : 'ℹ'}
              </span>
              <span style={itemLabelStyle(item.severity)}>{item.label}</span>
              {item.detail && (
                <span style={itemDetailStyle}>{item.detail}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

// ───── styles ────────────────────────────────────────────────────────────────

const emptyStateStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '4px 10px',
  borderRadius: 999,
  background: '#dcfce7',
  color: '#166534',
  fontSize: '0.72rem',
  fontWeight: 600,
  margin: '0 0 8px 0',
  cursor: 'default',
};

const panelStyle = (hasError: boolean): React.CSSProperties => ({
  border: '1px solid var(--border-color)',
  borderLeft: `3px solid ${hasError ? '#dc2626' : '#d97706'}`,
  borderRadius: 6,
  background: 'var(--bg-secondary)',
  margin: '0 0 10px 0',
  overflow: 'hidden',
});

const headerStyle: React.CSSProperties = {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '6px 12px',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  textAlign: 'left',
  fontFamily: 'inherit',
};

const headerLabelStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  fontSize: '0.8rem',
  fontWeight: 700,
  color: 'var(--text-primary)',
};

const countPillStyle = (hasError: boolean): React.CSSProperties => ({
  fontSize: '0.7rem',
  fontWeight: 700,
  padding: '2px 8px',
  borderRadius: 999,
  background: hasError ? '#fee2e2' : '#fef3c7',
  color: hasError ? '#991b1b' : '#92400e',
});

const listStyle: React.CSSProperties = {
  margin: 0,
  padding: 0,
  listStyle: 'none',
  borderTop: '1px solid var(--border-color)',
};

const itemRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '6px 12px',
  fontSize: '0.78rem',
};

const severityIconStyle = (severity: NoteDeviationSeverity): React.CSSProperties => ({
  fontSize: '0.85rem',
  color: severity === 'error' ? '#991b1b' : severity === 'warning' ? '#92400e' : '#1e40af',
  flexShrink: 0,
});

const itemLabelStyle = (severity: NoteDeviationSeverity): React.CSSProperties => ({
  color: severity === 'error' ? '#991b1b' : severity === 'warning' ? '#92400e' : 'var(--text-primary)',
  fontWeight: 500,
  flex: 1,
});

const itemDetailStyle: React.CSSProperties = {
  color: 'var(--text-secondary)',
  fontSize: '0.72rem',
  fontVariantNumeric: 'tabular-nums',
};

export default NoteDeviationPanel;
